import { db } from '@/api/firebase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatNumber } from '@/lib/utils';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useMainContext } from '@/context/main-context';
import { Loader } from 'lucide-react';

import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function StudentPayment({ student, groups, open, setOpen }) {
  const { courses, uid, activeBranchId } = useMainContext();
  const { toast } = useToast();

  const getDefaultValues = () => {
    const course = courses.find((c) => c.id === groups[0]?.courseId);
    return {
      name: student?.fullName || '',
      amount: course?.coursePrice || '',
      course: groups?.length > 0 ? groups[0]?.id : '',
      method: 'cash',
      studentId: '',
      branchId: activeBranchId || 'default',
      timestamp: new Date(),
    };
  };

  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: getDefaultValues() });

  const selectedCourse = watch('course');

  useEffect(() => {
    if (!selectedCourse) return;

    const group = groups.find((g) => g.id === selectedCourse);
    if (!group) return;

    const course = courses.find((c) => c.id === group.courseId);
    if (course?.coursePrice) {
      setValue('amount', course.coursePrice);
    }
  }, [selectedCourse, groups, courses, setValue]);

  useEffect(() => {
    reset(getDefaultValues());
  }, [student, groups, courses, reset]);

  const onSubmit = async (data) => {
    try {
      const userHistoryPaymentCollection = collection(
        db,
        `users/${uid}/paymentHistory`
      );
      const paymentDocRef = await addDoc(userHistoryPaymentCollection, {
        ...data,
        studentId: student?.id,
      });

      const paymentEntry = {
        paymentId: paymentDocRef.id,
        name: data.name,
        amount: data.amount,
        course: data.course,
        method: data.method,
        branchId: activeBranchId || 'default',
        timestamp: data.timestamp,
      };

      const studentRef = doc(db, 'students', student?.id);
      await updateDoc(studentRef, {
        paymentHistory: arrayUnion(paymentEntry),
      });

      reset();
      setOpen(false);
      toast({
        title: "To'lov muvaffaqiyat qo'shildi",
      });
    } catch (error) {
      console.error('Error adding payment:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>To'lov qo'shish</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-2 w-full">
            <div>
              <Label>Ism familiya</Label>
              <Input
                type="text"
                readOnly
                disabled={isSubmitting}
                {...register('name', {
                  required: "Bu yerni to'ldirish talab qilinadi",
                })}
                placeholder="Alex Johnson"
              />
              {errors.name && (
                <p className="text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="flex flex-col space-y-1 mt-1">
              <Label id="timestamp">Sana</Label>
              <Controller
                disabled={isSubmitting}
                name="timestamp"
                control={control}
                render={({ field }) => (
                  <ReactDatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    dateFormat="dd.MM.yyyy"
                    className="w-full py-2 px-3 border rounded-md bg-background"
                  />
                )}
              />
            </div>

            {/* <Controller
              disabled={isSubmitting}
              name="timestamp"
              control={control}
              defaultValue={new Date()}
              render={({ field }) => (
                <div>
                  <Label id="timestamp">Sana</Label>
                  <I18nProvider locale="ru-RU">
                    <InputDatePicker
                      formattedDate={field.value}
                      setFormattedDate={field.onChange}
                      ariaLabelledby="timestamp"
                      notFormat={true}
                    />
                  </I18nProvider>
                </div>
              )}
            /> */}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-2 w-full">
            <div className="w-full">
              <Label>Narx</Label>
              <div className="relative">
                <Controller
                  disabled={isSubmitting}
                  name="amount"
                  control={control}
                  rules={{
                    required: "Bu yerni to'ldirish talab qilinadi",
                    min: 0,
                  }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Input
                      type="text"
                      value={formatNumber(value)}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, '');
                        onChange(rawValue);
                      }}
                      placeholder="500 000"
                      ref={ref}
                    />
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm text-muted-foreground peer-disabled:opacity-50">
                  uzs
                </span>
                {errors.amount && (
                  <p className="text-red-500">{errors.amount.message}</p>
                )}
              </div>
            </div>

            <Controller
              disabled={isSubmitting}
              name="method"
              control={control}
              render={({ field }) => (
                <div>
                  <Label>To'lov usuli</Label>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="To'lov usuli" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Naqd</SelectItem>
                      <SelectItem value="credit_card">Kredit karta</SelectItem>
                      <SelectItem value="bank_transfer">
                        Bank o'tkazmasi
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.method && (
                    <p className="text-red-500">{errors.method.message}</p>
                  )}
                </div>
              )}
            />
          </div>

          <Controller
            disabled={isSubmitting}
            name="course"
            control={control}
            render={({ field }) => (
              <div>
                <Label>Guruhni tanlang</Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Guruhni tanlang" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((group) => {
                      const courseTitle = courses.find(
                        (item) => item.id === group?.courseId
                      )?.courseTitle;
                      return (
                        <SelectItem value={group.id} key={group.id}>
                          {courseTitle} #{group.groupNumber}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {errors.course && (
                  <p className="text-red-500">{errors.course.message}</p>
                )}
              </div>
            )}
          />

          {/* Submit Button */}
          <Button
            disabled={isSubmitting}
            className="flex items-center gap-1.5"
            type="submit"
          >
            {isSubmitting && <Loader className="w-3 h-3 animate-spin" />}
            <span>To'lov qilish</span>
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default StudentPayment;
